import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Skeleton,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useMemo, useState, useCallback } from "react";
import { getAllColumnsWithoutDimensions } from "../../components/Filter/FilterData/columnConfig";
import { numberFormatter } from "../../helper/Reports/common";
import { useAmazonPerformanceChart, usePerformanceChart } from "./dashboardHooks";
import { AMAZON_OVERVIEW_METRICS } from "./OverviewCardSection/amazonOverviewConfig";

/**
 * @param {object} props
 * @param {boolean} [props.useAmazonOverview]
 * @param {string[]} [props.selectedPortfolios]
 */
const PerformanceChart = ({
  useAmazonOverview = false,
  selectedPortfolios = [],
  leftMetricKey,
  rightMetricKey,
  onLeftMetricKeyChange,
  onRightMetricKeyChange,
  timePeriod: timePeriodProp,
  onTimePeriodChange,
} = {}) => {
  const allColumns = useMemo(() => {
    if (useAmazonOverview) return AMAZON_OVERVIEW_METRICS;
    return getAllColumnsWithoutDimensions(false, []);
  }, [useAmazonOverview]);

  const [internalXAxis, setInternalXAxis] = useState(
    () => allColumns.find((column) => column.key === "spends") || allColumns[0]
  );
  const [internalYAxis, setInternalYAxis] = useState(
    () =>
      allColumns.find((column) => column.key === "revenue") ||
      allColumns[1] ||
      allColumns[0]
  );
  const [internalTimePeriod, setInternalTimePeriod] = useState("date");
  const xAxis = leftMetricKey
    ? allColumns.find((column) => column.key === leftMetricKey) || internalXAxis
    : internalXAxis;
  const yAxis = rightMetricKey
    ? allColumns.find((column) => column.key === rightMetricKey) || internalYAxis
    : internalYAxis;
  const timePeriod = timePeriodProp ?? internalTimePeriod;

  const amazonChart = useAmazonPerformanceChart(
    timePeriod,
    selectedPortfolios,
    useAmazonOverview
  );
  const platformChart = usePerformanceChart(
    timePeriod,
    null,
    !useAmazonOverview
  );
  const { chartData, loading } = useAmazonOverview
    ? amazonChart
    : platformChart;

  // Generate time period categories from fetched data
  const getTimeCategories = useMemo(() => {
    if (loading || !chartData || chartData.length === 0) {
      // Return default categories while loading
      if (timePeriod === "date") {
        return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      } else if (timePeriod === "week") {
        return ["Week 1", "Week 2", "Week 3", "Week 4"];
      } else {
        return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      }
    }

    // Extract categories from chartData (dateLabel is already formatted in the hook)
    return chartData.map((item) => item.dateLabel || "");
  }, [chartData, loading, timePeriod]);

  // Generate series data from fetched data
  const series = useMemo(() => {
    if (loading || !chartData || chartData.length === 0) {
      return [
        {
          name: xAxis?.shortTitle || "",
          data: [],
          yAxisIndex: 0,
        },
        {
          name: yAxis?.shortTitle || "",
          data: [],
          yAxisIndex: 1,
        },
      ];
    }

    return [
      {
        name: xAxis?.shortTitle || "",
        data: chartData.map((item) => item[xAxis?.key] || 0),
        yAxisIndex: 0,
      },
      {
        name: yAxis?.shortTitle || "",
        data: chartData.map((item) => item[yAxis?.key] || 0),
        yAxisIndex: 1,
      },
    ];
  }, [chartData, xAxis, yAxis, loading]);

  const formatValue = useCallback((value, column) => {
    return numberFormatter(
      value,
      column?.isCurrencyFormat,
      column?.format || column?.type,
      column?.maximumFractionDigits
    );
  }, []);

  const formatTooltipValue = useCallback((val, column) => {
    return numberFormatter(
      val,
      column?.isCurrencyFormat,
      column?.format || column?.type,
      column?.maximumFractionDigits
    );
  }, []);

  const options = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      offsetX: 0,
      offsetY: 0,
    },
    plotOptions: {
      area: {
        dataLabels: {
          offsetX: 0,
        },
      },
    },

    stroke: {
      curve: "smooth",
      width: 2,
    },

    dataLabels: { enabled: false },

    markers: {
      size: 4,
      strokeWidth: 2,
      hover: { size: 6 },
    },

    colors: ["#FF66C4", "#6C63FF"],

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.4,
        stops: [40, 80, 100],
      },
    },

    // -------------------------
    //     X-AXIS
    // -------------------------
    xaxis: {
      categories: getTimeCategories,
      title: {
        text: "",
      },
      labels: {
        style: {
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },

    // -------------------------
    //      Y-AXES (Left & Right)
    // -------------------------
    yaxis: [
      {
        title: {
          text: xAxis?.shortTitle,
          offsetX: -20,
        },
        labels: {
          formatter: (value) => formatValue(value, xAxis),
        },
      },
      {
        opposite: true,
        title: {
          text: yAxis?.shortTitle,
          offsetX: 20,
        },
        labels: {
          formatter: (value) => formatValue(value, yAxis),
        },
      },
    ],

    grid: { borderColor: "#eee" },

    legend: {
      show: false,
    },

    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const series1Value = series[0][dataPointIndex];
        const series2Value = series[1] ? series[1][dataPointIndex] : null;
        const series1Name = w.globals.seriesNames[0];
        const series2Name = w.globals.seriesNames[1];
        const category = w.globals.categoryLabels[dataPointIndex];

        let tooltipContent = `
          <div style="padding: 10px; background: #fff; border: 1px solid #e0e0e0; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
            <div style="margin-bottom: 8px; font-weight: 600; font-size: 13px; color: #333;">${category}</div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="width: 10px; height: 10px; background: #FF66C4; border-radius: 50%; display: inline-block; margin-right: 8px; flex-shrink: 0;"></span>
              <span style="font-size: 12px; color: #333;">${series1Name}: <strong style="color: #000;">${formatTooltipValue(
          series1Value,
          xAxis  
        ) || ""}</strong></span>
            </div>
        `;

        if (series2Value !== null && series2Value !== undefined) {
          tooltipContent += `
            <div style="display: flex; align-items: center;">
              <span style="width: 10px; height: 10px; background: #6C63FF; border-radius: 50%; display: inline-block; margin-right: 8px; flex-shrink: 0;"></span>
              <span style="font-size: 12px; color: #333;">${series2Name}: <strong style="color: #000;">${formatTooltipValue(
            series2Value,
            yAxis  
          ) || ""}</strong></span>
            </div>
          `;
        }

        tooltipContent += `</div>`;
        return tooltipContent;
      },
    },
  }), [getTimeCategories, xAxis, yAxis, formatValue, formatTooltipValue]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{
          fontSize: "16px",
          mb: 1,
        }}
      >
        Performance Analysis
      </Typography>

      <Paper
        sx={{
          p: 2,
          pt: 2.5,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #E0E0E0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* y1-axis Searchable Dropdown with Purple Dot */}
            <Autocomplete
              size="small"
              value={xAxis}
              onChange={(event, newValue) => {
                if (!newValue) return;
                if (onLeftMetricKeyChange) onLeftMetricKeyChange(newValue.key);
                else setInternalXAxis(newValue);
              }}
              options={allColumns}
              getOptionLabel={(option) => option?.shortTitle}
              sx={{
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Left-axis"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Box
                        sx={{ display: "flex", alignItems: "center",pl: 1, mr: 0 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#FF66C4",
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#FF66C4",
                      flexShrink: 0,
                    }}
                  />
                  {option?.shortTitle}
                </Box>
              )}
            />

            {/* y2-axis Searchable Dropdown with Blue Dot */}
            <Autocomplete
              size="small"
              value={yAxis}
              onChange={(event, newValue) => {
                if (!newValue) return;
                if (onRightMetricKeyChange) onRightMetricKeyChange(newValue.key);
                else setInternalYAxis(newValue);
              }}
              options={allColumns}
              getOptionLabel={(option) => option?.shortTitle}
              sx={{
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Right-axis"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Box
                      sx={{ display: "flex", alignItems: "center",pl: 1, mr: 0 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#6C63FF",
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#6C63FF",
                      flexShrink: 0,
                    }}
                  />
                  {option?.shortTitle}
                </Box>
              )}
            />

            {/* Time Period Options - ToggleButtonGroup */}
            <ToggleButtonGroup
              value={timePeriod}
              exclusive
              onChange={(e, newValue) => {
                if (newValue === null) return;
                if (onTimePeriodChange) onTimePeriodChange(newValue);
                else setInternalTimePeriod(newValue);
              }}
              aria-label="time period"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 0.65,
                  border: "1px solid",
                  borderColor: "divider",
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  },
                },
              }}
            >
              <ToggleButton size="small" value="date" aria-label="date">
                Day
              </ToggleButton>
              <ToggleButton size="small" value="week" aria-label="week">
                Week
              </ToggleButton>
              <ToggleButton size="small" value="month" aria-label="month">
                Month
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type="area"
            height={320}
          />
        )}
      </Paper>
    </Box>
  );
};

export default PerformanceChart;
